/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { Table, Spin } from "@duxton-web/ui"
import { ColumnProps } from "@duxton-web/ui/es/table"
import { BulkUploadTask } from "types/delv-platform-api/bulkUploadTask"
import PastUploadStatus from "pages/menu/detail/bulkUpload/pastUploads/pastUploadTable/PastUploadStatus"
import { TroyIcon } from "components/common"
import { BULK_UPLOAD_TABLE_RECORD_LIMIT } from "constants/menu_bulk_upload"
import dayjs from "dayjs"
import { useTranslations } from "common/utils/i18n"
import { isOngoingTask } from "store/duck/bulkUpload/utils"

type PropTypes = {
  isFetching: boolean
  bulkUploadTasks: BulkUploadTask[]
}

const commonTextCss = css`
  font-size: 14px;
  color: #1a1a1a;
`

const tableWrapCss = css`
  .dui-table-thead {
    tr > th {
      padding: 20px;
      ${commonTextCss};
      font-weight: 500;
    }
  }

  .dui-table-tbody {
    tr > td {
      padding: 16px 20px;
      cursor: unset !important;

      > span {
        ${commonTextCss};
        font-weight: normal;
      }

      > a {
        font-weight: 500;
      }
    }
  }
`

const emptyTableWrapCss = css`
  padding-top: 64px;
  padding-bottom: 32px;
`

const emptyTableTextCss = css`
  font-size: 16px;
  margin-top: 24px;
  color: #707070;
`

const fileNameCss = css`
  ${commonTextCss};
  word-break: break-all;
`

const linkDownloadErrorCSVCss = css`
  margin-top: 8px;

  a {
    cursor: pointer;
  }
`

const loadingIconCss = css`
  padding: 32px 0;
`

const PastUploadTable = ({ isFetching, bulkUploadTasks }: PropTypes) => {
  const [
    fileNameColText,
    itemCountColText,
    bulkTaskStatusColText,
    dateColText,
    downloadErrorFileText,
    emptyTableText,
    emailColText,
    uploadedFileURLColText,
    downloadText,
  ] = useTranslations([
    "bulkUpload.historyTable.columns.fileName",
    "bulkUpload.historyTable.columns.itemCount",
    "bulkUpload.historyTable.columns.status",
    "bulkUpload.historyTable.columns.date",
    "bulkUpload.historyTable.columns.downloadError",
    "bulkUpload.historyTable.emptyTable",
    "bulkUpload.historyTable.columns.userEmail",
    "bulkUpload.historyTable.columns.uploadedFileURL",
    "common.download",
  ])
  const columns: ColumnProps<BulkUploadTask>[] = [
    {
      title: fileNameColText?.toUpperCase(),
      dataIndex: "uploadFileName",
      render: (fileName: string) => (
        <span css={fileNameCss}>{fileName || "--"}</span>
      ),
      width: "25%",
    },
    {
      title: itemCountColText?.toUpperCase(),
      render: (task: BulkUploadTask) => (
        <div css={commonTextCss}>
          {task && !isOngoingTask(task) && task.totalRowCount
            ? task.totalRowCount
            : "--"}
        </div>
      ),
      width: "15%",
    },
    {
      title: bulkTaskStatusColText?.toUpperCase(),
      render: (task: BulkUploadTask) => (
        <div>
          <PastUploadStatus
            status={task.status}
            totalRowCount={task.totalRowCount}
            successRowCount={task.successRowCount}
          />
          {task.errorReportURL && (
            <div css={linkDownloadErrorCSVCss}>
              <a href={task.errorReportURL}>{downloadErrorFileText}</a>
            </div>
          )}
        </div>
      ),
      width: "15%",
    },
    {
      title: dateColText?.toUpperCase(),
      key: "createdAt",
      dataIndex: "createdAt",
      render: (createdAt: string) => (
        <div css={commonTextCss}>
          {createdAt
            ? dayjs(new Date(createdAt)).format("DD MMM YY HH:mm")
            : "--"}
        </div>
      ),
      width: "15%",
    },
    {
      title: emailColText?.toUpperCase(),
      dataIndex: "userEmail",
      render: (userEmail: string) => (
        <div css={commonTextCss}>{userEmail || "__"}</div>
      ),
      width: "15%",
    },
    {
      title: uploadedFileURLColText?.toUpperCase(),
      dataIndex: "uploadedFileURL",
      render: (uploadedFileURL: string) =>
        uploadedFileURL && <a href={uploadedFileURL}>{downloadText}</a>,
      width: "15%",
    },
  ]
  return (
    <Table
      rowKey={(task: BulkUploadTask) => task.taskID}
      css={tableWrapCss}
      columns={columns}
      dataSource={bulkUploadTasks}
      pagination={{
        pageSize: BULK_UPLOAD_TABLE_RECORD_LIMIT,
        hideOnSinglePage: true,
      }}
      locale={{
        emptyText: (
          <div>
            {isFetching ? (
              <div css={loadingIconCss}>
                <Spin />
              </div>
            ) : (
              <div css={emptyTableWrapCss}>
                <TroyIcon type="bulkUploadEmptyTable" />
                <div css={emptyTableTextCss}>{emptyTableText}</div>
              </div>
            )}
          </div>
        ),
      }}
    />
  )
}

export default PastUploadTable
